import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Button, Dropdown } from '@dynamon-as/ui';
import type {
    DropdownCategory,
    DropdownMenuItem,
    DropdownOption,
    SelectChangeEvent,
} from '@dynamon-as/ui';
import { useDepartment } from '@hooks';
import { FeatureToggles } from '@lib/featureToggles';
import { buildFullLogoutOptions } from '@auth/utils';
import type { FC } from 'react';

const LOGOUT_VALUE = 'logout';

const LANGUAGES: DropdownOption[] = [
    { value: 'NO', label: 'Norsk (Bokmål)' },
    { value: 'DK', label: 'Dansk' },
    { value: 'NN', label: 'Norsk (Nynorsk)' },
    { value: 'EN', label: 'English' },
];

const LANGUAGE_MENU: DropdownCategory = {
    category: 'Språk',
    items: LANGUAGES,
};

const LOGOUT_MENU_ITEM: DropdownMenuItem = {
    value: LOGOUT_VALUE,
    label: 'Logg ut',
};

/**
 * Header controls rendered in the `@dynamon-as/ui` Header `actions`
 * slot: a unified user menu (triggered by the account email) with the
 * language selection and logout, or a login button when
 * unauthenticated.
 */
const HeaderActions: FC = () => {
    const { loginWithRedirect, isAuthenticated, user, logout } = useAuth0();
    const { department } = useDepartment();
    const [selectedLanguage, setSelectedLanguage] = useState<string>(
        () => localStorage.getItem('app_language') ?? 'NO',
    );

    useEffect(() => {
        localStorage.setItem('app_language', selectedLanguage);
        window.dispatchEvent(new Event('languageChange'));
    }, [selectedLanguage]);

    // Default the language to the organization's country when the user
    // has no explicit language preference stored yet.
    useEffect(() => {
        const storedLang = localStorage.getItem('app_language');
        const orgLang = department?.org?.countryRules?.countryCode;
        if (!storedLang && orgLang) {
            setSelectedLanguage(orgLang);
        }
    }, [department?.org?.countryRules?.countryCode]);

    // The language options are only available while the translation
    // feature toggle is enabled.
    const menuItems: DropdownMenuItem[] =
        FeatureToggles.FEATURE_TOGGLE_TRANSLATION_ENABLED.isEnabled()
            ? [LANGUAGE_MENU, LOGOUT_MENU_ITEM]
            : [LOGOUT_MENU_ITEM];

    const handleMenuChange = (event: SelectChangeEvent<unknown>): void => {
        const value = String(event.target.value);

        if (value === LOGOUT_VALUE) {
            logout(buildFullLogoutOptions());
            return;
        }

        setSelectedLanguage(value);
    };

    if (!isAuthenticated) {
        return (
            <Button
                text="Logg Inn"
                onClick={() => loginWithRedirect()}
            />
        );
    }

    return (
        <Dropdown
            id="header-user-menu"
            title="Brukermeny"
            placeholder={user?.email ?? ''}
            variant="borderless"
            menuItems={menuItems}
            onChange={handleMenuChange}
        />
    );
};

export default HeaderActions;
